import React from 'react';
import {VERDICT_COULEURS} from '../tokens.js';
import {contourAbribac, lisereAbribac} from './carte/carte.selectors.js';

/*
 * Silhouette d'abribac, dessin retenu « piste S » (viewBox 24x24) : caisson plus haut que large, visière de capot qui
 * déborde, trappe de dépôt en relief sur le dessus, d'un seul bloc dans la couleur du verdict. Les tracés sont exportés
 * pour que la couche canvas de la carte les rejoue en Path2D (sprites) : une seule source de géométrie.
 * Le glyphe (✓ / clé / ×) est blanc, à l'intérieur du caisson, et double la couleur : l'information n'est jamais portée
 * par la couleur seule. Pas de pastille ni de trappe blanche (null : la couche canvas les saute).
 * Liseré blanc et contour coloré sont en px d'écran (barème par taille dans `carte.selectors.js`), pas en unités de viewBox.
 */

const NI = VERDICT_COULEURS.ni.marqueur;
const GLYPHE_CX = 12;
const GLYPHE_CY = 14.4;
const GLYPHE_DEMI = 4.2; // demi-côté du carré du glyphe, en unités de viewBox
const S_CLE = (GLYPHE_DEMI * 2.2) / 24; // la clé du handoff (viewBox 24) ramenée à la taille du glyphe

export const ABRIBAC = {
    corps: 'M4 6.5H8V5.1Q8 4.3 8.8 4.3H15.2Q16 4.3 16 5.1V6.5H20V8.3H18.5V19.7Q18.5 20.5 17.7 20.5H6.3Q5.5 20.5 5.5 19.7V8.3H4Z',
    trappe: null,
    socle: {x: 4, y: 20.7, w: 16, h: 1.5, rx: 0.75},
    pastille: null,
    glyphe: {cx: GLYPHE_CX, cy: GLYPHE_CY, couleur: '#FFFFFF'},
    ancreY: 21.4, // le point géographique est au milieu du socle
};

export const GLYPHES = {
    ras: {trace: 'M9.5 14.4l1.8 1.8 3.3-3.3', mode: 'stroke'},
    imp: {trace: 'M9.7 12.1l4.6 4.6M14.3 12.1l-4.6 4.6', mode: 'stroke'},
    pb: {
        trace: 'M22.7 19l-9.1-9.1c.9-2.3.4-5-1.5-6.9-2-2-5-2.4-7.4-1.3L9 6 6 9 1.6 4.7C.4 7.1.9 10.1 2.9 12.1c1.9 1.9 4.6 2.4 6.9 1.5l9.1 9.1c.4.4 1 .4 1.4 0l2.3-2.3c.5-.4.5-1.1.1-1.4z',
        mode: 'fill',
        transform: {tx: GLYPHE_CX - 12 * S_CLE, ty: GLYPHE_CY - 12 * S_CLE, s: S_CLE},
    },
};
export const LARGEUR_GLYPHE = 1.9;

/** Couleur de verdict (`tokens.js`) -> code de verdict ; null si la couleur n'est pas un verdict connu. */
export function verdictDeCouleur(couleur) {
    const trouve = Object.entries(VERDICT_COULEURS).find(([, v]) => v.pastille === couleur);
    return trouve ? trouve[0] : null;
}

export default function AbribacIcon({size = 32, couleur, glyphe}) {
    const g = glyphe && glyphe !== 'ni' ? GLYPHES[glyphe] : null;
    const ni = !g;
    const verdict = verdictDeCouleur(couleur);
    const encre = verdict ? VERDICT_COULEURS[verdict].texte : couleur;
    const {socle} = ABRIBAC;
    const blanc = ABRIBAC.glyphe.couleur;
    const u = 24 / size; // px d'écran -> unités de viewBox
    const contour = contourAbribac(size) * u;
    const lisere = lisereAbribac(size) * u;
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" style={{display: 'block', overflow: 'visible'}}>
            <path d={ABRIBAC.corps} fill="#FFFFFF" stroke="#FFFFFF" strokeWidth={contour + 2 * lisere} strokeLinejoin="round"/>
            <path d={ABRIBAC.corps} fill={ni ? NI.corps : couleur} stroke={ni ? NI.contour : encre} strokeWidth={ni ? u : contour} strokeLinejoin="round"/>
            <rect x={socle.x} y={socle.y} width={socle.w} height={socle.h} rx={socle.rx} fill="#FFFFFF" fillOpacity="0.6"/>
            {g && (g.mode === 'fill'
                ? <path d={g.trace} fill={blanc} transform={`translate(${g.transform.tx} ${g.transform.ty}) scale(${g.transform.s})`}/>
                : <path d={g.trace} fill="none" stroke={blanc} strokeWidth={LARGEUR_GLYPHE} strokeLinecap="round" strokeLinejoin="round"/>)}
        </svg>
    );
}
